// Constants
import { AllowedChatDataAttributesForDeserialize } from "@ms/uno-constants/lib/local/ChatConstants";
// Utilities
import { Temporal } from "@js-temporal/polyfill";
import { sanitizeContent } from "@ms/uno-utilities/lib/local/ContentSanitizationUtilities";
import { createTrustedHtml } from "@ms/uno-utilities/lib/local/TrustedTypeUtilities";
import { deserializeEmbeddedHtmlMentions } from "../../../utilities/ChatAtMentionsUtilities";
const GROUPING_TIME_THRESHOLD = 3; // minutes
// Helper: format a Temporal date/time based on when it was sent
// - Today: "5:00 PM"
// - Before today (this year): "8/27 5:00 PM"
// - Previous year: "8/27/2024 5:00 PM"
export function formatChatTimestamp(temporal) {
    const now = Temporal.Now.instant();
    const timeZone = Temporal.Now.timeZoneId();
    // Get the dates in the user's timezone for comparison
    const nowZoned = now.toZonedDateTimeISO(timeZone);
    const messageZoned = temporal.toZonedDateTimeISO(timeZone);
    const todayStart = nowZoned.startOfDay();
    const messageDate = messageZoned.startOfDay();
    // Check if message is from today
    const isToday = todayStart.equals(messageDate);
    // Check if message is from the same year
    const isSameYear = nowZoned.year === messageZoned.year;
    let rawTime;
    if (isToday) {
        // Today: show only time
        rawTime = temporal.toLocaleString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            timeZone
        });
    } else if (isSameYear) {
        // This year but before today: show month/day and time
        rawTime = temporal.toLocaleString(undefined, {
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone
        });
    } else {
        // Previous year: show full date and time
        rawTime = temporal.toLocaleString(undefined, {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone
        });
    }
    // Remove a single comma + optional space (common in en locales) so "8/15, 5:00 PM" -> "8/15 5:00 PM"
    return rawTime.replace(/,\s?/, " ");
}
/**
 * Helper to determine if a message should show the header (author info + timestamp)
 * @param currentMessage The current message
 * @param previousMessage The previous message in the thread, if any
 */ export function shouldShowHeader(currentMessage, previousMessage) {
    if (!previousMessage) {
        return true;
    }
    const isSameAuthor = currentMessage.createdBy.user.id === previousMessage.createdBy.user.id;
    const timeDiff = currentMessage.createdDateTime.since(previousMessage.createdDateTime);
    const withinTimeThreshold = timeDiff.round({
        largestUnit: "minutes"
    }).minutes <= GROUPING_TIME_THRESHOLD;
    return !(isSameAuthor && withinTimeThreshold);
}
/**
 * Processes the HTML content of a chat message to make it ready for rendering
 * @param message The chat message to process
 * @param currentUserId The ID of the current user
 * @param getUserById Function to retrieve user information by ID
 * @returns The processed and sanitized HTML content as a string
 */ export function processMessageHtmlContent(message, currentUserId, getUserById) {
    // First sanitize HTML content
    const sanitizedContent = sanitizeContent(message.content, {
        allowedAttributes: AllowedChatDataAttributesForDeserialize
    });
    // Parse html into document and do all processing on the same Document object for performance
    const trustedHtmlString = createTrustedHtml(sanitizedContent, true);
    let doc = new DOMParser().parseFromString(trustedHtmlString, "text/html");
    // Process mentions in the message content
    doc = deserializeEmbeddedHtmlMentions(doc, message.properties.mentions, currentUserId, getUserById);
    // Process links to add target="_blank" and rel="noopener noreferrer"
    doc = processLinksInDocument(doc);
    // Return HTML content
    return doc.body.innerHTML;
}
/**
 * Adds target="_blank" and rel="noopener noreferrer" to all anchor elements with href in the document.
 * @param doc The HTML document to process
 * @returns The updated document
 */ export function processLinksInDocument(doc) {
    const links = doc.querySelectorAll("a[href]");
    links.forEach((link)=>{
        const href = link.getAttribute("href");
        if (href) {
            link.setAttribute("target", "_blank");
            link.setAttribute("rel", "noopener noreferrer");
            // Add title of the url if it doesn't exist
            if (!link.hasAttribute("title")) {
                link.setAttribute("title", href);
            }
        }
    });
    return doc;
}
